import { EnvironmentInjector, Injectable, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { type Route, type PreloadingStrategy } from '@angular/router';
import { Observable, ReplaySubject, finalize, of, tap } from 'rxjs';

type PreloadTask = {
  id: number;
  load: () => Observable<unknown>;
  order: number;
  priority: number;
  result: ReplaySubject<unknown>;
};

@Injectable({ providedIn: 'root' })
export class OrderedRoutePreloadingStrategy implements PreloadingStrategy {
  readonly #injector = inject(EnvironmentInjector);
  readonly #activeTask = signal<PreloadTask | undefined>(undefined);
  readonly #queue: PreloadTask[] = [];
  #nextOrder = 0;

  readonly #taskRunner = rxResource<unknown, PreloadTask | undefined>({
    injector: this.#injector,
    params: () => this.#activeTask(),
    stream: ({ params: task }) => {
      if (!task) return of(undefined);

      return task.load().pipe(
        tap({
          next: (value) => task.result.next(value),
          complete: () => task.result.complete(),
          error: (error) => task.result.error(error),
        }),
        finalize(() => this.#finishTask(task.id)),
      );
    },
  });

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (route.data?.['preload'] !== true) return of(null);

    const result = new ReplaySubject<unknown>(1);
    const task = {
      id: this.#nextOrder,
      load,
      order: this.#nextOrder++,
      priority: this.#readPriority(route),
      result,
    } satisfies PreloadTask;

    this.#queue.push(task);
    this.#queue.sort(
      (left, right) =>
        left.priority - right.priority || left.order - right.order,
    );
    this.#drainQueue();

    return result.asObservable();
  }

  #readPriority(route: Route) {
    const priority = route.data?.['preloadPriority'];
    return typeof priority === 'number' ? priority : Number.MAX_SAFE_INTEGER;
  }

  #drainQueue() {
    if (this.#activeTask()) return;

    const nextTask = this.#queue.shift();
    if (!nextTask) return;

    this.#activeTask.set(nextTask);
    this.#taskRunner.status();
  }

  #finishTask(taskId: number) {
    const activeTask = this.#activeTask();
    if (!activeTask || activeTask.id !== taskId) return;

    this.#activeTask.set(undefined);
    this.#drainQueue();
  }
}
